<template>
  <div class="directory-actions">
    <DottedDropdown :id="'dir-action-' + id">
      <li v-if="canEdit">
        <button
          type="button"
          class="btn btn--transparent"
          @click="edit()"
        >{{ $t('action.edit') }}</button>
      </li>
      <li v-if="canEditPublished">
        <button
          type="button"
          class="btn btn--transparent"
          @click="action(ACTION.editPublished)"
        >{{ $t('action.edit') }}</button>
        <button
          type="button"
          class="btn btn--transparent"
          @click="remove"
        >{{ $t('action.remove') }}</button>
      </li>
      <li v-if="showUndo">
        <button
          type="button"
          class="btn btn--transparent"
          @click="action(ACTION.undoRemove)"
        >{{ $t('action.undo') }}</button>
      </li>
      <li v-if="canCancel">
        <button
          type="button"
          class="btn btn--transparent"
          @click="action(ACTION.cancelRequest)"
        >{{ $t('directory.action.cancelRequest') }}</button>
      </li>
      <li>
        <button
          type="button"
          class="btn btn--transparent"
          @click="openCommentsHistory"
        >{{ $t('directory.action.commentsHistory') }}</button>
      </li>
      <li>
        <button
          type="button"
          class="btn btn--transparent"
          @click="showAttachments()"
        >{{ $t('attachments') }}</button>
      </li>
    </DottedDropdown>
    <Modal
      :modalId="`${id}-dir-action`"
      ref="alertActionMsg"
    >
      <div
        class="font-bold"
        slot="content"
      >
        <div v-html="alertMsg" />
      </div>
      <div slot="btns">
        <button
          type="button"
          class="btn btn--orange close-reveal-modal"
          @click="doIt()"
        >{{ $t('action.yesContinue') }}</button>
      </div>
    </Modal>
    <Modal
      :modalId="`${id}-dir-attachments`"
      ref="attachmentsDetail"
      :modalType="'medium-modal'"
      :showCloseBtn="false"
    >
      <p class="font-bold">{{ $t('directory.label.documents') }}</p>
      <EditAttachments
        class="directory-actions__edit-attachments"
        ref="editAttachments"
        :canDelete="false"
        :canEdit="false"
        :sourceId="attachmentId"
        :showEmptyMsg="true"
        :emptyMsg="$t('directory.msg.emptyDocHint')"
      />
      <div
        slot="btns"
        class="row align-center nav_btn"
      >
        <button
          type="button"
          class="btn btn--orange"
          data-close
          aria-label="Close reveal"
        >{{ $t('action.ok') }}</button>
      </div>
    </Modal>
  </div>
</template>

<script>
import { orderBy } from 'lodash'
import { mapGetters, mapActions, mapMutations } from 'vuex'

import DottedDropdown from '@/controls/DottedDropdown'
import EditAttachments from '@/controls/EditAttachments'
import Modal from '@/controls/Modal'

import { deleteAllDocuments } from '@/api/docs/request'
import { ActionTypes, directoryRouteNames, systemUserId } from '@/utils/consts'
import mutationTypes from '@/store/mutation-types'
import { DIR_STATUS, DIR_ACTION } from './consts'
import { deleteDocsAfterDate, getCancellable } from './utils'
import directoryEditBtnMixin from './directoryEditBtnMixin'

export default {
  name: 'DirectoryActions',
  components: {
    DottedDropdown,
    EditAttachments,
    Modal,
  },
  mixins: [directoryEditBtnMixin],
  props: {
    item: {
      type: Object,
      default: () => {},
    },
    openLifecycleFun: {
      type: Function,
      default: () => {},
    },
  },
  methods: {
    ...mapActions('directories', [
      'submitDirectory',
      'deleteDirectory',
    ]),
    ...mapMutations('directories', {
      updateField: mutationTypes.DIRECTORY_SET_FIELD_BY_PATH,
    }),
    async remove () {
      this.navToDetail(this.id, { fromPage: this.isTenant ? directoryRouteNames.tenantDirectories : directoryRouteNames.tenancyDirectories })
    },
    _updateField (path, value) {
      this.updateField({ path, value })
    },
    async cancelRequest () {
      if (
        this.item.action === DIR_ACTION.ADD &&
        this.item.status !== DIR_STATUS.PUBLISHED
      ) {
        await this.deleteDirectory({
          id: this.id,
        })
        await await deleteAllDocuments(this.id)
      }
      if (
        [
          DIR_STATUS.CART,
          DIR_STATUS.REJECTED,
          DIR_STATUS.SUBMITTED,
          DIR_STATUS.APPR,
          DIR_STATUS.PROCESSED,
        ].includes(this.item.status) &&
        this.item.action === DIR_ACTION.REMOVE
      ) {
        this.item.removeDate = null
        if (Object.keys(this.currentDirectory).length !== 0) {
          this._updateField(['removeDate'], null)
        }
        let request = this.item
        request.specificAttrs = true
        await this.submitDirectory({
          id: this.id,
          action: DIR_ACTION.ADD,
          status: DIR_STATUS.PUBLISHED,
          needUpdate: true,
          refresh: false,
          request,
        })
        if (this.item.publishedAt) {
          await deleteDocsAfterDate(this.id, this.item.publishedAt)
          this.$refs.editAttachments.loadFiles()
        }
        // delete duplicate item
        if (this.item.child) {
          await this.deleteDirectory({
            id: this.item.child.id,
          })
          await deleteAllDocuments(this.item.child.id)
        }
      }
      this.$emit('refresh:all')
    },
    async undoRemove () {
      await this.submitDirectory({
        id: this.id,
        status: DIR_STATUS.PUBLISHED,
        action: DIR_ACTION.ADD,
        needUpdate: true,
        refresh: false,
        request: {
          removeDate: null,
          specificAttrs: true,
        },
        headerAction: ActionTypes.directories.undo,
      })
      this.$emit('refresh:all')
    },
    showAttachments () {
      this.$refs.attachmentsDetail.open()
    },
    openCommentsHistory () {
      this.openLifecycleFun(this.rejectHistories)
    },
  },
  computed: {
    ...mapGetters('user', ['isTenant', 'isEngineer']),
    ...mapGetters('directories', ['currentDirectory', 'canCurrUserApprove', 'canCurrUserCreate', 'canCurrUserRemove']),
    id () {
      return this.item.id
    },
    showUndo () {
      let removedByUser = false
      const histories = orderBy(this.item.histories, ['createdAt'], ['desc'])
      const isRemoved = (this.item.status === DIR_STATUS.PUBLISHED && this.item.action === DIR_ACTION.REMOVE)
      if (histories && isRemoved && histories[0].who.id) {
        removedByUser = histories[0].who.id !== systemUserId
      }

      return this.canCurrUserRemove && removedByUser
    },
    canCancel () {
      return getCancellable({
        canCurrUserCreate: this.canCurrUserCreate,
        directory: this.item,
        isTenant: this.isTenant,
        isEngineer: this.isEngineer,
      })
    },
    rejectHistories () {
      return this.item.histories.filter(history => DIR_STATUS.REJECTED === history.status)
    },
    attachmentId () {
      return this.item.child && this.item.action === DIR_ACTION.REMOVE && this.item.status !== DIR_STATUS.PUBLISHED ? this.item.child.id : this.id
    },
  },
}
</script>

<style lang="scss" scoped>
.slot-content span {
  font-weight: bold;
}
.is-submenu-item {
  & > button.btn {
    width: 100%;
    text-align: left
  }
}
.directory-actions {
  &__edit-attachments {
    & ::v-deep th.description, & ::v-deep td.description {
      width: auto
    }
    & ::v-deep th.uploaded-date {
      width: 165px
    }
  }
}
</style>
